import React, {
  Fragment, useState, useRef, useCallback, useEffect, useMemo
} from "react";
import {
  Card, CardBody, Row, Col, Nav, NavItem, NavLink,
  TabContent, TabPane, Button, ButtonGroup, Collapse,
  FormGroup, Label, Input
} from "reactstrap";
import {
  InstantSearch,
  Hits,
  Pagination,
  useHits,
  Configure
} from "react-instantsearch";
import { createElasticsearchAdapter } from "../../utils/elasticsearchAdapter";
import {
  CustomSearchBox,
  CustomStats,
  CustomSortBy,
  CustomRefinementList,
  CustomClearRefinements,
  CustomPagination
} from "./CustomSearchComponents";
import ApplicantsHits     from "./Hits";
import Notifications      from "../commons/Notifications";
import ApplicantsProvider from "./ApplicantsContext";
import Dashboard          from "../course/Dashboard";
import CreateUserModal    from "./CreateUserModal";
import ApplicantsTools    from "./ApplicantsTools";
import { FormatApplicant } from "./ApplicantHelper";
import SelectionBoard     from "../course/SelectionBoard";
import KanbanBoard        from "../course/kanban/KanbanBoard";
import _ from "lodash";
import classnames from "classnames";

/* -------------  helpers ------------- */
const AASM_STATES = [
  { name: "pending",  label: "Pendiente" },
  { name: "approved", label: "Aprobado" },
  { name: "rejected", label: "Rechazado" }
];
const TYPE_LABELS = {
  "minisite::postulation":   "Minisitio",
  "trabajando::postulation": "Trabajando"
};
const makeCombinedStatusItemComponent = (aasmStates, workFlow) => {
  const all    = [...aasmStates, ...workFlow.map(wf => ({ name: wf.name, label: wf.name }))];
  const labels = Object.fromEntries(all.map(({ name, label }) => [name, label]));
  return function StatusItem({ label, count, active, disabled, onClick }) {
    return (
      <div
        className={`sk-refinement-option${active ? " is-active" : ""}${disabled ? " sk-disabled" : ""}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .6 : 1, padding: "4px 0" }}
        onClick={() => !disabled && onClick()}
      >
        <input type="checkbox" readOnly checked={active} style={{ marginRight: 8 }} />
        <span>{labels[label] || label}</span>
        <span style={{ float: "right", fontWeight: 500, minWidth: 20, textAlign: "right" }}>
          {count ?? 0}
        </span>
      </div>
    );
  };
};

// HitsWrapper component to handle Instantsearch hits
const HitsWrapper = ({
  viewMode,
  hits,
  setHits,
  selected,
  setSelected,
  checkAll,
  setCheckAll,
  isChecked,
  handleCheckApplicant,
  handleCheckAll,
  updateHit,
  reloadAfterUpdate,
  reloadQuery,
  notify,
  props,
  courseId,
  workFlow,
  isAdmin,
  currentUserRole,
  selectionMap,
  boardRef,
  isPostulation
}) => {
  const { hits: instantHits } = useHits();
  
  useEffect(() => {
    // Convert Instantsearch hits to the format expected by existing components
    // InstantSearch hits have data at root level, but we need _source structure
    // The adapter spreads source data at root level AND keeps _source, so we use _source if available
    const convertedHits = instantHits.map(hit => {
      // Prefer _source if it exists (it's the original structure from Elasticsearch)
      // Otherwise, reconstruct from the hit data (which has been spread at root level)
      let source;
      if (hit._source && Object.keys(hit._source).length > 0) {
        // Use _source directly if it exists and is not empty
        source = hit._source;
      } else {
        // Reconstruct _source from spread data
        // Remove InstantSearch-specific fields
        const { objectID, _id, _source, ...sourceData } = hit;
        source = sourceData;
      }
      
      // Get the ID from the hit
      const hitId = hit._id || hit.objectID || hit.id || source?.id;
      
      return {
        _id: hitId,
        _source: source
      };
    });
    
    // Always update when instantHits changes
    // setHits is stable, so we don't need it in dependencies
    setHits(convertedHits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantHits, isPostulation]);

  // Update selected when hits change
  useEffect(() => {
    if (checkAll && hits.length > 0) {
      const allSelected = hits.map(h => FormatApplicant(h, isPostulation));
      if (!_.isEqual(selected, allSelected)) {
        setSelected(allSelected);
      }
    }
  }, [hits, checkAll, isPostulation, selected, setSelected]);

  // For postulations, job_head users can see the list
  // For applicants, job_head users only see the board
  const isJobHead = Array.isArray(currentUserRole)
    ? currentUserRole.includes("job_head")
    : currentUserRole === "job_head";
  
  const shouldShowList = viewMode === "list" && hits.length > 0 && (isPostulation || !isJobHead);

  if (shouldShowList) {
    return (
      <>
        <ApplicantsTools
          isPostulation={isPostulation}
          courseId={courseId}
          workFlow={workFlow}
          reloadQuery={reloadQuery}
          showNotification={notify}
          applicantsSelected={selected}
          checkAllApplicant={checkAll}
          handleCheckAllApplicant={handleCheckAll}
          isAdmin={isAdmin}
          boardRef={boardRef}
          selectionMap={selectionMap}
        />
        <ApplicantsHits
          {...props}
          courseId={courseId}
          reloadQuery={reloadQuery}
          showNotification={notify}
          hits={hits}
          isAdmin={isAdmin}
          isChecked={isChecked}
          handleCheckApplicant={handleCheckApplicant}
          updateHit={updateHit}
          reloadAfterUpdate={reloadAfterUpdate}
          selectionMap={selectionMap}
          boardRef={boardRef}
          currentUserRole={currentUserRole}
        />
      </>
    );
  }

  return null;
};

const makeTypeItemComponent = map => {
  return function TypeItem({ label, count, active, disabled, onClick }) {
    return (
      <div
        className={`sk-refinement-option${active ? " is-active" : ""}${disabled ? " sk-disabled" : ""}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .6 : 1, padding: "4px 0" }}
        onClick={() => !disabled && onClick()}
      >
        <input type="checkbox" readOnly checked={active} style={{ marginRight: 8 }} />
        <span>{map[label] ?? label}</span>
        <span style={{ float: "right", fontWeight: 500, minWidth: 20, textAlign: "right" }}>
          {count ?? 0}
        </span>
      </div>
    );
  };
};

const buildFilterConfigs = (prefix, workFlow = [], isPostulation = false) => {
  const filters = [];
  if (isPostulation) {
    filters.push({
      id: "status_combined",
      label: "Estado",
      field: "status_combined",
      placeholder: "Buscar estado…",
      workflow: true,
      aasmStates: AASM_STATES,
      workFlow,
      itemComponent: makeCombinedStatusItemComponent(AASM_STATES, workFlow)
    });
    filters.push({
      id: "postulation_type",
      label: "Origen",
      field: "type.raw",
      placeholder: "Buscar origen…",
      itemComponent: makeTypeItemComponent(TYPE_LABELS)
    });
  }
  filters.push(
    { id: "institucion",  label: "Casa de Estudios", field: `${prefix}personal_educations.education_institution.raw`, placeholder: "Buscar institución…`" },
    { id: "carrera",      label: "Carrera",          field: `${prefix}personal_educations.education_career.raw`,      placeholder: "Buscar carrera…" },
    { id: "ultimo_cargo", label: "Último cargo",     field: `${prefix}laboral_experiences.company_position.raw`,       placeholder: "Buscar cargo…" },
    { id: "region",       label: "Región de residencia", field: `${prefix}personal_information.region.label.raw`,     placeholder: "Buscar región…" }
  );
  return filters;
};

const buildSearchFields = prefix => [
  `${prefix}email`,
  `${prefix}personal_information.first_name`,
  `${prefix}personal_information.middle_name`,
  `${prefix}personal_information.last_name`,
  prefix.includes("applicant.") ? "applicant.cv_keywords" : "cv_keywords"
];

const statusOptionsMerger = ({ buckets, filterConfig }) => {
  if (!filterConfig.workflow) return buckets;
  const names = [
    ...filterConfig.aasmStates.map(a => a.name),
    ...(filterConfig.workFlow || []).map(wf => wf.name)
  ];
  const byKey = Object.fromEntries((buckets || []).map(b => [b.key, b]));
  return [
    ...names.map(k => byKey[k] ?? { key: k, doc_count: 0 }),
    ...(buckets || []).filter(b => !names.includes(b.key))
  ];
};

/* -------------  componente ------------- */
const ApplicantsIndex = props => {
  const {
    route, isPostulation = false, showFilters = false,
    course, workFlow = [], isAdmin, currentUserRole
  } = props;

  const courseId   = course?.id;
  const userPrefix = isPostulation ? "applicant.user." : "user.";

  // Stabilize workFlow to prevent unnecessary recreations
  // workFlow is an array that may change reference on each render from Rails
  const stableWorkFlow = useMemo(
    () => workFlow || [],
    [JSON.stringify(workFlow || [])]
  );

  // Memoize filterConfigs and searchFields to prevent unnecessary searchClient recreation
  const filterConfigs = useMemo(
    () => buildFilterConfigs(userPrefix, stableWorkFlow, isPostulation),
    [userPrefix, stableWorkFlow, isPostulation]
  );
  const searchFields = useMemo(
    () => buildSearchFields(userPrefix),
    [userPrefix]
  );

  const [activeTab, setActiveTab]     = useState("1");
  const [viewMode,  setViewMode]      = useState("list");
  const [hits,      setHits]          = useState([]);
  const [selected,  setSelected]      = useState([]);
  const [checkAll,  setCheckAll]      = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openFilterId, setOpenFilterId] = useState(null);
  const notificationsRef = useRef(null);
  const boardRef         = useRef(null);
  const [selectionMap, setSelectionMap] = useState({});

  const enableFilters = isPostulation || showFilters;

  const isProcessClosed = (course?.aasm_state || course?.aasmState) === "closed";
  const isHead = Array.isArray(currentUserRole)
    ? currentUserRole.includes("job_head")
    : currentUserRole === "job_head";
  const canEditKanban = !isProcessClosed && !isHead;

  // Store default filters and search fields
  const defaultFilters = useMemo(() => {
    const filters = [{ term: { "user.role_names": "candidatos" } }];
    if (viewMode === "board") {
      filters.push({ term: { "aasm_state": "approved" } });
    }
    return filters;
  }, [viewMode]);

  // Store sort option in a ref to avoid recreating the client
  const [sortOption, setSortOption] = useState({ field: "_score", order: "desc" });
  const sortOptionRef = useRef(sortOption);
  
  // Update ref when sort option changes
  useEffect(() => {
    sortOptionRef.current = sortOption;
  }, [sortOption]);

  // Use refs to store current values so we don't need to recreate the client
  const defaultFiltersRef = useRef(defaultFilters);
  const searchFieldsRef = useRef(searchFields);
  
  // Update refs when values change
  useEffect(() => {
    defaultFiltersRef.current = defaultFilters;
  }, [defaultFilters]);
  
  useEffect(() => {
    searchFieldsRef.current = searchFields;
  }, [searchFields]);

  // Listen for sort changes from CustomSortBy
  useEffect(() => {
    const handleSortChange = (event) => {
      const option = event.detail;
      setSortOption({ field: option.field, order: option.order });
    };
    
    window.addEventListener('search:sort', handleSortChange);
    return () => {
      window.removeEventListener('search:sort', handleSortChange);
    };
  }, []);

  // Store filterConfigs in a ref to avoid recreating searchClient
  const filterConfigsRef = useRef(filterConfigs);
  useEffect(() => {
    filterConfigsRef.current = filterConfigs;
  }, [filterConfigs]);

  // Create search client with Elasticsearch adapter
  // Only recreate when route changes to avoid resetting pagination
  // Note: filterConfigs is accessed via ref inside the wrapper, so it doesn't need to be a dependency
  const searchClient = useMemo(() => {
    // Ensure route ends with /_search
    const searchRoute = route.endsWith('/_search') ? route : `${route}/_search`;
    
    const adapter = createElasticsearchAdapter(searchRoute, {
      httpHeaders: { "X-CSRF-TOKEN": window.Hupe8.token },
      timeout: 20000
    });
    
    // Wrap the search method to inject defaultFilters, queryFields, and customSort dynamically
    const originalSearch = adapter.search.bind(adapter);
    adapter.search = async (requests) => {
      // Get all filter attributes that need aggregations from ref (current value)
      const filterAttributes = filterConfigsRef.current.map(config => config.field);
      
      // Get current values from refs
      const modifiedRequests = requests.map(req => ({
        ...req,
        params: {
          ...req.params,
          customSort: sortOptionRef.current,
          defaultFilters: defaultFiltersRef.current,
          queryFields: searchFieldsRef.current,
          // Ensure facets are always requested for filter attributes
          facets: [...(req.params.facets || []), ...filterAttributes].filter((v, i, a) => a.indexOf(v) === i)
        }
      }));
      return originalSearch(modifiedRequests);
    };
    
    return adapter;
  }, [route]); // Only recreate when route changes

  const handleBoardChange = useCallback(
    () => setSelectionMap(boardRef.current?.getSelectionMap() || {}),
    []
  );
  
  const reloadAfterUpdate = useCallback(() => {
    // Trigger search refresh - will be handled by InstantSearch
    window.dispatchEvent(new Event('search:refresh'));
  }, []);
  
  const isChecked = hit => selected.some(a => a.value === String(hit._source?.id || hit._id));
  
  const handleCheckApplicant = e => {
    const { value, checked } = e.target;
    const hit = hits.find(h => String(h._source?.id || h._id) === value);
    if (!hit) return;
    setSelected(prev =>
      checked ? [...prev, FormatApplicant(hit, isPostulation)]
        : prev.filter(a => a.value !== value)
    );
  };
  
  const handleCheckAll = e => {
    const checked = e.target.checked;
    setCheckAll(checked);
    setSelected(checked ? hits.map(h => FormatApplicant(h, isPostulation)) : []);
  };
  
  const reloadQuery = useCallback(() => {
    window.dispatchEvent(new Event('search:refresh'));
  }, []);
  const notify      = (t, m, tp) => notificationsRef.current.addNotifications(t, m, tp);
  const updateHit = updated => {
    setHits(prev => prev.map(h => String(h._source.id) === String(updated.id)
      ? { ...h, _source: updated } : h));
  };

  return (
    <ApplicantsProvider>
      {course && (
        <Nav pills className="mb-3">
          <NavItem>
            <NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")}>
              <i className="fas fa-users me-2" /> Postulaciones
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")}>
              <i className="fas fa-tachometer-alt me-2" /> Dashboard
            </NavLink>
          </NavItem>
        </Nav>
      )}

      <InstantSearch 
        key={`is-${viewMode}`}
        searchClient={searchClient}
        indexName={isPostulation ? "postulations" : "applicants"}
        future={{ preserveSharedStateOnUnmount: true }}
        routing={false}
      >
        <Configure hitsPerPage={viewMode === "list" ? 100 : 1000} />
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            {course && (
              <>
                <SelectionBoard
                  ref={boardRef}
                  courseId={courseId}
                  vacancies={course.vacancies}
                  onChange={handleBoardChange}
                  currentUserRole={currentUserRole}
                />

                <Row className="mb-2">
                  <Col md="6" />
                  <Col md="6" className="text-end">
                    <FormGroup switch className="d-inline-flex align-items-center">
                      <Label className="me-2 mb-0 small">Lista</Label>
                      <Input
                        type="switch"
                        checked={viewMode === "board"}
                        onChange={e => setViewMode(e.target.checked ? "board" : "list")}
                      />
                      <Label className="ms-2 mb-0 small">Tablero</Label>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            <Card className="mb-3 material-shadow">
              <CardBody>
                <Row>
                  <Col md="6">
                    <CustomSearchBox 
                      queryFields={searchFields}
                      placeholder="Buscar"
                    />
                    <CustomStats />
                  </Col>
                  <Col />
                  {enableFilters && (
                    <Col md="3" className="text-end">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        className="mb-2"
                        style={{ minWidth: 110 }}
                        onClick={() => setFiltersOpen(o => !o)}
                      >
                        <i className="fas fa-filter me-2" /> Filtros
                      </Button>
                      <CustomSortBy 
                        options={[
                          { label: "Mayor coincidencia", field: "_score", order: "desc", defaultOption: true },
                          { label: "Menor coincidencia", field: "_score", order: "asc"  },
                          { label: "ID descendente",     field: "id", order: "desc" },
                          { label: "ID ascendente",      field: "id", order: "asc"  }
                        ]}
                        onSortChange={(option) => {
                          // Update sort in search client - handled by adapter
                          window.dispatchEvent(new CustomEvent('search:sort', { detail: option }));
                        }}
                      />
                    </Col>
                  )}
                </Row>

                {enableFilters && (
                  <Collapse isOpen={filtersOpen} className="mb-2 filtros-compactos">
                    <div className="bg-light border rounded p-3 shadow-sm">
                      <Row>
                        {filterConfigs.map(f => (
                          <Col xs={12} key={f.id} className="mb-2">
                            <Button
                              color="link"
                              className="p-0 mb-1 filtro-capitalizado"
                              style={{ fontWeight: "bold", fontSize: 13, textAlign: "left",
                                width: "100%", letterSpacing: "1px", textTransform: "uppercase" }}
                              onClick={() =>
                                setOpenFilterId(openFilterId === f.id ? null : f.id)
                              }
                            >
                              {f.label}
                              <span className="float-end">
                                {openFilterId === f.id ? "▲" : "▼"}
                              </span>
                            </Button>
                            <Collapse isOpen={openFilterId === f.id}>
                              <CustomRefinementList
                                attribute={f.field}
                                searchPlaceholder={f.placeholder}
                                itemComponent={f.itemComponent}
                                searchable={false}
                              />
                            </Collapse>
                          </Col>
                        ))}
                      </Row>
                      <Row className="mt-2">
                        <Col className="d-flex justify-content-end">
                          <CustomClearRefinements translations={{ "reset.clear_all": "Limpiar filtros" }} />
                          <Button
                            color="light"
                            size="sm"
                            outline
                            className="ms-2"
                            onClick={() => setFiltersOpen(false)}
                          >
                            Cerrar
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </Collapse>
                )}
              </CardBody>
            </Card>

            <Notifications ref={notificationsRef} />

            <HitsWrapper
              viewMode={viewMode}
              hits={hits}
              setHits={setHits}
              selected={selected}
              setSelected={setSelected}
              checkAll={checkAll}
              setCheckAll={setCheckAll}
              isChecked={isChecked}
              handleCheckApplicant={handleCheckApplicant}
              handleCheckAll={handleCheckAll}
              updateHit={updateHit}
              reloadAfterUpdate={reloadAfterUpdate}
              reloadQuery={reloadQuery}
              notify={notify}
              props={props}
              courseId={courseId}
              workFlow={workFlow}
              isAdmin={isAdmin}
              currentUserRole={currentUserRole}
              selectionMap={selectionMap}
              boardRef={boardRef}
              isPostulation={isPostulation}
            />

            {viewMode === "list" && hits.length === 0 && (
              <div className="text-center mt-4">
                <p>No se encontraron resultados</p>
                <ButtonGroup>
                  <CreateUserModal showNotification={notify} reloadQuery={reloadQuery} />
                </ButtonGroup>
              </div>
            )}

            {viewMode === "list" && hits.length > 0 && (
              <CustomPagination />
            )}

            {course && viewMode === "board" && hits.length > 0 && (
              <KanbanBoard
                hits={hits.filter(h => h._source?.aasm_state === "approved")}
                workFlow={workFlow}
                courseId={courseId}
                reloadSearch={reloadQuery}
                currentUser={currentUserRole}
                showNotification={notify}
                switchToList={() => setViewMode("list")}
                boardRef={boardRef}
                isProcessClosed={isProcessClosed}
                canEdit={canEditKanban}
              />
            )}
          </TabPane>

          <TabPane tabId="2">
            {course && hits.length > 0 && (
              <Dashboard hits={hits} {...props} />
            )}
          </TabPane>
        </TabContent>
      </InstantSearch>
    </ApplicantsProvider>
  );
};

export default ApplicantsIndex;
